import { useId, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import { Slot } from 'radix-ui'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import { ComposeInput, ComposeTextarea } from '@/components/compose-field'

type ButtonVariant = ComponentProps<typeof Button>['variant']

export type ActionPopoverNote = {
  /** Screen-reader name of the note field. */
  label: string
  placeholder?: string
  /** The confirm button stays disabled until the note has this many characters (1 = required). */
  minLength?: number
  maxLength?: number
  /** One line instead of a two-line textarea. */
  singleLine?: boolean
}

/**
 * A quick action that opens a small anchored popover instead of a modal (USA-1536), or a bottom
 * sheet on phones, the same way every picker does (USA-1527): an optional
 * title, any extra fields, an optional reason note, and one confirm button. Dismiss, holds, waivers
 * and confirmations all use it so every quick action looks and behaves the same.
 *
 * Extra fields render above the note; give one `order-last` to place it below the note instead.
 *
 * `onConfirm` receives the trimmed note and a `close` callback that closes and clears the note.
 * Call `close` from the mutation's `onSuccess` to keep the popover (and the note) on failure.
 */
export function ActionPopover({
  trigger,
  anchorOnly = false,
  title,
  note,
  children,
  confirmLabel,
  confirmVariant = 'default',
  pending = false,
  pendingLabel,
  canConfirm = true,
  align = 'start',
  secondaryAction,
  isolate = false,
  focusNote = !children,
  open: controlledOpen,
  onOpenChange,
  onConfirm,
  contentClassName,
}: {
  trigger: ReactNode
  /** The trigger only positions the popover (it opens from a menu item via `open`), not toggles it. */
  anchorOnly?: boolean
  title?: string
  note?: ActionPopoverNote
  children?: ReactNode
  /** Without `confirmLabel`/`onConfirm` it only shows its content (details behind a click). */
  confirmLabel?: string
  confirmVariant?: ButtonVariant
  pending?: boolean
  pendingLabel?: string
  canConfirm?: boolean
  align?: 'start' | 'center' | 'end'
  /** A second, quieter button beside confirm, e.g. Remove. */
  secondaryAction?: ReactNode
  /** Keep clicks and keys inside from reaching what it sits in (a clickable row or link). */
  isolate?: boolean
  /** Desktop focus starts in the note; otherwise on the first field (default: note only when alone). */
  focusNote?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onConfirm?: (note: string, close: () => void) => void
  /** Sizes the desktop popover, e.g. a wider one for a search with results. */
  contentClassName?: string
}) {
  const titleId = useId()
  const sheetRef = useRef<HTMLDivElement>(null)
  const sheet = useMediaQuery(PHONE_QUERY)
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [value, setValue] = useState('')
  const open = controlledOpen ?? uncontrolledOpen
  const trimmed = value.trim()
  const noteReady = !note || trimmed.length >= (note.minLength ?? 0)
  const ready = canConfirm && noteReady && !pending

  // Closing keeps a typed note (Escape, a click outside, a failed save); `close` clears it.
  const setOpen = (next: boolean) => {
    if (!next && pending) return
    setUncontrolledOpen(next)
    onOpenChange?.(next)
  }

  const confirm = () => {
    if (!ready || !onConfirm) return
    onConfirm(trimmed, () => {
      setValue('')
      setOpen(false)
    })
  }

  const fieldProps = {
    'aria-label': note?.label,
    placeholder: note?.placeholder,
    value,
    maxLength: note?.maxLength ?? 2000,
    required: (note?.minLength ?? 0) > 0,
    disabled: pending,
  }

  const form = (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        confirm()
      }}
    >
      {title && !sheet && (
        <p id={titleId} className="text-sm font-medium">
          {title}
        </p>
      )}
      {children}
      {note &&
        (note.singleLine ? (
          <ComposeInput
            autoFocus={!sheet && focusNote}
            {...fieldProps}
            onChange={(event) => setValue(event.target.value)}
            className={sheet ? 'h-11' : 'h-8 text-sm'}
          />
        ) : (
          <ComposeTextarea
            autoFocus={!sheet && focusNote}
            {...fieldProps}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault()
                confirm()
              }
            }}
            className={sheet ? 'min-h-24' : 'min-h-16 text-sm'}
          />
        ))}
      {onConfirm && (
        <div
          className={cn(
            'order-last flex items-center gap-2',
            secondaryAction ? 'justify-between' : 'justify-end',
            sheet && secondaryAction && 'flex-col-reverse items-stretch',
          )}
        >
          {secondaryAction}
          <LoadingButton
            type="submit"
            size={sheet ? 'default' : 'sm'}
            variant={confirmVariant}
            loading={pending}
            loadingText={pendingLabel}
            disabled={!ready}
            className={sheet ? 'h-11 w-full' : undefined}
          >
            {confirmLabel}
          </LoadingButton>
        </div>
      )}
    </form>
  )

  // Events from portaled content still bubble through React to the trigger's parents.
  const isolated = (node: ReactNode) =>
    isolate ? (
      <span className="contents" onClick={stopEvent} onKeyDown={stopEvent}>
        {node}
      </span>
    ) : (
      node
    )

  // Phones get a bottom sheet: thumb-reachable, full width, and never clipped by the screen edge.
  if (sheet) {
    return isolated(
      <>
        {anchorOnly ? (
          trigger
        ) : (
          <Slot.Root
            aria-haspopup="dialog"
            aria-expanded={open}
            data-state={open ? 'open' : 'closed'}
            onClick={() => setOpen(true)}
          >
            {trigger}
          </Slot.Root>
        )}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            ref={sheetRef}
            side="bottom"
            aria-describedby={undefined}
            // Focus the sheet, not its first field: the keyboard opens only when a field is tapped.
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              sheetRef.current?.focus()
            }}
            className="max-h-[85dvh] gap-0 overflow-y-auto rounded-t-xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 outline-none"
          >
            <SheetTitle className="pb-3 pr-10 text-base font-semibold">{title ?? confirmLabel ?? ''}</SheetTitle>
            {form}
          </SheetContent>
        </Sheet>
      </>,
    )
  }

  return isolated(
    <Popover open={open} onOpenChange={setOpen}>
      {anchorOnly ? (
        <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      <PopoverContent
        align={align}
        collisionPadding={12}
        aria-labelledby={title ? titleId : undefined}
        className={cn('w-[min(20rem,calc(100vw-1.5rem))] p-3', contentClassName)}
      >
        {form}
      </PopoverContent>
    </Popover>,
  )
}

function stopEvent(event: { stopPropagation: () => void }) {
  event.stopPropagation()
}
